import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { MaskedLine } from "@/components/Motion";

const WorldHero = ({ testid, accent, kicker, l1, l2, sub, cta1, cta2, note, children }) => (
  <section data-testid={testid} className="relative px-5 sm:px-8 pt-32 pb-16 sm:pt-40 sm:pb-20 overflow-hidden">
    <div
      className="absolute -top-40 right-0 h-[500px] w-[500px] rounded-full blur-[120px] pointer-events-none"
      style={{ backgroundColor: `${accent}0F` }}
    />
    <div className="relative mx-auto max-w-[1400px] grid grid-cols-1 items-center gap-10 lg:grid-cols-12 lg:gap-12">
      <div className="lg:col-span-7">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="inline-flex items-center gap-2.5 rounded-full border border-black/10 bg-white px-4 py-1.5 font-jbmono text-[11px] uppercase tracking-[0.25em]"
          style={{ color: accent }}
        >
          <span className="h-2 w-2 rounded-full animate-pulse" style={{ backgroundColor: accent }} />
          {kicker}
        </motion.div>
        <h1 className="mt-7 font-cabinet font-bold tracking-tight leading-[1.06] text-4xl sm:text-5xl lg:text-6xl">
          <MaskedLine delay={0.25}>{l1}</MaskedLine>
          <MaskedLine delay={0.4}>
            <span style={{ color: accent }}>{l2}</span>
          </MaskedLine>
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.6 }}
          className="mt-6 max-w-xl text-base lg:text-lg text-[#888888] font-jakarta leading-relaxed"
        >
          {sub}
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.75 }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          {cta1.to?.startsWith("#") ? (
            <a
              href={cta1.to}
              onClick={cta1.onClick}
              data-testid={`${testid}-cta-primary`}
              className="group inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-jakarta font-bold text-white transition-all duration-300 hover:gap-4"
              style={{ backgroundColor: accent }}
            >
              {cta1.label} <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </a>
          ) : (
            <Link
              to={cta1.to}
              data-testid={`${testid}-cta-primary`}
              className="group inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-jakarta font-bold text-white transition-all duration-300 hover:gap-4"
              style={{ backgroundColor: accent }}
            >
              {cta1.label} <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          )}
          <a
            href={cta2.to}
            onClick={cta2.onClick}
            data-testid={`${testid}-cta-secondary`}
            className="inline-flex items-center rounded-full border border-black/15 px-6 py-3.5 text-sm font-jakarta font-semibold transition-colors duration-300 hover:bg-black/[0.04]"
          >
            {cta2.label}
          </a>
          {note && (
            <span className="font-jbmono text-[10px] uppercase tracking-[0.2em] text-[#999999]">{note}</span>
          )}
        </motion.div>
      </div>
      <motion.div
        initial={{ opacity: 0, y: 32 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 0.55 }}
        className="lg:col-span-5 flex items-center lg:justify-end"
      >
        {children}
      </motion.div>
    </div>
  </section>
);

export default WorldHero;
